import { useQuery, useMutation } from "@apollo/client";
import {
  Grid,
  Group,
  List,
  Paper,
  Text,
  Title,
  Select,
  Stack
} from "@mantine/core";
import { useMediaQuery } from '@mantine/hooks';
import { useState, useEffect, useRef } from "react";
import { Link, useParams } from "react-router-dom";

import RankingCard from "../../components/assets/RankingCard/RankingCard";
import PaginationBar from "../../components/assets/PaginationBar/PaginationBar";
import ContestInfoCard from "../../components/assets/ContestInfoCard/ContestInfoCard";
import UserContestCard from "../../components/assets/UserContestCard/UserContestCard";
import CountdownTimer from "../../components/assets/CountdownTimer/CountdownTimer"
import UniversityRankingCard from "../../components/assets/UniversityRankingCard/UniversityRankingCard";
import SearchBar from "../../components/common/SearchBar/SearchBar";

import { CURRENT_USER, GET_UNIVERSITIES } from "../../queries/auth";
import { ADD_LOG } from "../../queries/user-analytics";
import {
  GET_PARTICIPANTS,
  UPDATE_WEEKLY_RETURN_OF_PARTICIPANT,
} from "../../queries/contest";

import "./LeaderboardPage.css";

export default function LeaderboardPage() {
  const { loading, error, data } = useQuery(CURRENT_USER);
  const [userid, setUserid] = useState(null);
  const { name } = useParams();
  const [currentLeaderboardPage, setLeaderboardPage] = useState(0);
  const [firstRankingIndex, setFirstRankingIndex] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [leaderboard, setLeaderboard] = useState([]);
  const [universities, setUniversities] = useState([]);
  const [selectedUserData, setSelectedUserData] = useState(null);
  const isMobile = useMediaQuery('(max-width: 1090px)');
  // added for tracking user data
  const [logEventMutation] = useMutation(ADD_LOG, {
    onCompleted: ({ addLog }) => {
        // Access the HTTP response directly
        const { status, message } = addLog;
        if (status === "success") {
            console.log("Event logged successfully:", message);
        } else {
            console.error("Failed to log event:", message);
        }
    },
    onError: (error) => {
        console.error("Error logging event:", error.message);
    }
});

const logEvent = (eventType, additionalData, _userid) => {
    const eventData = {
        userid: Number(_userid),
        event_type: eventType,
        timestamp: new Date().toISOString(),
        additional_data: JSON.stringify(additionalData)

    };

    logEventMutation({ variables: eventData });
};
const logEventCalledRef = useRef(false);


  useEffect(() => {
    if (loading) console.log('Loading user...')
    else if (error) { setUserid(null) }
    else {
      // console.log(data);
      setUserid(data.currentUser.userid);
      if (logEventCalledRef.current === false)
                {
                    logEvent('LeaderBoardPage', data.currentUser, data.currentUser.userid); // added for tracking user data
                    logEventCalledRef.current = true;
                }
    }
  }, [loading, error, data])

  const [activeTab, setActiveTab] = useState('all');

  const { data: uniData } = useQuery(GET_UNIVERSITIES);
  const uniOptions = uniData?.getUniversities || [];

  const {
    loading: participantloading,
    error: participantError,
    data: participantData,
    refetch,
  } = useQuery(GET_PARTICIPANTS, {
    variables: { contestname: name, userid: -1 },
  });
  
  const [updateWeeklyReturnOfParticipant] = useMutation(
    UPDATE_WEEKLY_RETURN_OF_PARTICIPANT
  );

  useEffect(() => {
    if (participantloading) {
      console.log("loading leaderboard");
    } else if (participantError) {
      console.log(participantError);
      setLeaderboard([]);
    } else {
      // console.log(participantData.getParticipants);
      const newLeaderboard = participantData.getParticipants
          ? participantData.getParticipants.map((ranking, index) => [
              index + 1,
              ranking,
            ])
          : []
      setLeaderboard(newLeaderboard);
      
      
      // Count the number of students and their total returns in each university
      const universityData = newLeaderboard.reduce((acc, [, participant]) => {
        const university = participant.university;
        if (university) {
          if (!acc[university]) {
            acc[university] = { count: 0, totalReturn: 0 };
          }
          acc[university].count += 1;
          acc[university].totalReturn += participant.weekly_return;
        }
        return acc;
      }, {});
      
      // Convert the data object into an array of objects and calculate the average returns, then sort by returns in descending order
      const universitiesArray = Object.entries(universityData)
        .map(([university, { count, totalReturn }]) => ({
          university,
          count,
          // avgReturn: totalReturn / count,
          avgReturn: totalReturn / 25,
        }))
        .sort((a, b) => b.avgReturn - a.avgReturn);

      setUniversities(universitiesArray);
    }
  }, [participantloading, participantError, participantData, refetch]);

  useEffect(() => {
    if (name) {
      // Update weekly return via UPDATE_WEEKLY_RETURN_OF_PARTICIPANT mutation
      console.log("UPDATING");

      const updateAndRefetch = async () => {
        try {
          await updateWeeklyReturnOfParticipant({
            variables: {
              contestname: name,
            },
          });
          // After updating, refetch the participants data
          refetch();
        } catch (error) {
          console.error("Error updating weekly return:", error.message);
        }
      };

      // Initial update
      updateAndRefetch();

      // Set up the interval to update every minute
      const intervalId = setInterval(updateAndRefetch, 60000);

      // Clean up the interval on unmount
      return () => clearInterval(intervalId);
    }
  }, [name, refetch, updateWeeklyReturnOfParticipant]);

  const rankingsPerPage = 3;

  let filteredRankings = leaderboard
    ? leaderboard.filter((leaderboard) => {
      const matchName = leaderboard[1].alias.toLowerCase().includes(searchQuery.toLowerCase());
      const matchUniversity =
        activeTab === 'all' ||
        leaderboard[1].university === activeTab;
      return matchName && matchUniversity;
    })
    : null;
  let leaderboardPageCount = filteredRankings
    ? Math.ceil(filteredRankings.length / rankingsPerPage)
    : 1;

  const changeLeaderboardPage = (pageNumber) => {
    if (pageNumber !== currentLeaderboardPage) {
      setLeaderboardPage(pageNumber);
      setFirstRankingIndex(pageNumber * rankingsPerPage);
    }
  };

  const handleSearch = (value) => {
    setSearchQuery(value);
    setLeaderboardPage(0);
    setFirstRankingIndex(0);
  };

  const handleRankingCardClick = (user, rank) => {
    setSelectedUserData({ user, rank });
    // console.log("SELECTED: " + selectedUserData);
  };

  return (
    <div className="container">
      <Group className="back-to-contest">
        <Link to=".." style={{ color: "#afafaf" }}>
          ◀ &nbsp;Back to Contest
        </Link>
      </Group>
      <Group>
        <h2 className="h2 ranking-title-wrapper">
          <span>
            Ranking of&nbsp;
            <a href={"/contest/" + name}>
              {name.toUpperCase().split("_").join(" ")}
            </a>
            &nbsp;&nbsp;
          </span>
        </h2>
      </Group>
      <Stack spacing={36}>
        <Grid>
          {!isMobile && <Grid.Col span={8}>
            <CountdownTimer contestname={name} />
            <div className="callout callout-info">
              <h4>Instructions</h4>
              <span>
                <p>
                  During / After the contest is finished, you can view others' ranking and P/L
                  trend
                </p>
                <p>
                  If you suspect a user cheat by using an <i>explicitly</i>{" "}
                  disallowed library function, please report it to us via the
                  support email address.
                  {/* please report it by clicking on the "Report Cheating" toggle
                  under the suspicious solution. */}
                </p>
              </span>
            </div>
          </Grid.Col>}
          {isMobile && <Grid.Col span={12}>
            <CountdownTimer contestname={name} />
            <div className="callout callout-info">
              <h4>Instructions</h4>
              <span>
                <p>
                  During / After the contest is finished, you can view others' ranking and P/L
                  trend
                </p>
                <p>
                  If you suspect a user cheat by using an <i>explicitly</i>{" "}
                  disallowed library function, please report it to us via the
                  support email address.
                  {/* please report it by clicking on the "Report Cheating" toggle
                  under the suspicious solution. */} 
                </p>
              </span>
            </div>
          </Grid.Col>}
          {!isMobile && <Grid.Col span={4}>
            <ContestInfoCard userid={userid} contestname={name} refetchLeaderboard={refetch} />
          </Grid.Col>}
          {isMobile && <Grid.Col span={12}>
            <ContestInfoCard userid={userid} contestname={name} refetchLeaderboard={refetch} />
          </Grid.Col>}
        </Grid>

        <Paper
              shadow="sm"
              radius="md"
              withBorder
              pb="xs"
              mx="sm"
            >
            <Title p="xl" order={2}>
              University Ranking
              <Text style={{ fontSize: '14px', color: '#999' }}>
                  (Refreshes every minute)
              </Text>
            </Title>
            <Grid>
              <Grid.Col span={12}>
                <List size="xl" listStyleType="none">
                {universities.map(({ university, count, avgReturn }, index) => (
                  <UniversityRankingCard
                  university={university}
                  count={count}
                  avgReturn={avgReturn}
                  rank={index+1}
                  key={index}
                />
                ))}
                </List>
              </Grid.Col>
            </Grid>
        </Paper>
        
        <Grid>
          {!isMobile && <Grid.Col span={7}>
            <Paper
              shadow="sm"
              radius="md"
              withBorder
              pb="xs"
              mx="sm"
              style={{ height: "90%" }}
            >
              <Title p="xl" pb="sm" order={2}>
                Student Ranking
                <Text style={{ fontSize: '14px', color: '#999' }}>
                    (Refreshes every minute)
                </Text>
              </Title>
              <div className="search-bar-container">
                <SearchBar 
                  onSearch={handleSearch}
                  placeholder="Search for user..."
                  className="mb-md"
                  debounceDelay={300}
                />
              </div>
              <Select
                placeholder="Filter by university"
                data={[
                  { value: 'all', label: 'All' },
                  ...uniOptions.map(u => ({ value: u, label: u }))
                ]}
                value={activeTab}
                onChange={(val) => setActiveTab(val || 'all')}
                clearable
                mb="md"
                w={300}
                mx="auto"
              />
                  
              <List size="xl" center>
                {filteredRankings ? (
                  filteredRankings.length > 0 ? (
                    <List size="xl" center listStyleType="none">
                      {filteredRankings
                        .slice(
                          firstRankingIndex,
                          firstRankingIndex + rankingsPerPage
                        )
                        .map((ranking, index) => (
                          <div
                            index={index}
                            onClick={() =>
                              handleRankingCardClick(ranking[1], ranking[0])
                            }
                          >
                            <RankingCard
                              user={ranking[1]}
                              rank={ranking[0]}
                              key={index}
                              currUser={ranking[1].userid === userid}
                            />
                          </div>
                        ))}
                    </List>
                  ) : (
                    <p className="placeholder">No Participants</p>
                  )
                ) : (
                  <p className="placeholder">Loading...</p>
                )}
              </List>
            </Paper>

            <PaginationBar
              currentPage={currentLeaderboardPage}
              pageCount={leaderboardPageCount}
              handleOnClick={changeLeaderboardPage}
            />
          </Grid.Col>}
          {isMobile && <Grid.Col span={12}>
            <Paper
              shadow="sm"
              radius="md"
              withBorder
              pt="xl"
              pb="xs"
              mx="sm"
              style={{ minHeight: "85%" }}
            >
              <Title pl="lg" order={2}>
                Student Ranking
              </Title>
              <div className="search-bar-container">
                <SearchBar 
                  onSearch={handleSearch}
                  placeholder="Search for user..."
                  className="mb-md"
                  debounceDelay={300}
                />
              </div>
              <Select
                placeholder="Filter by university"
                data={[
                  { value: 'all', label: 'All' },
                  ...uniOptions.map(u => ({ value: u, label: u }))
                ]}
                value={activeTab}
                onChange={(val) => setActiveTab(val || 'all')}
                clearable
                mb="md"
                w={300}
                mx="auto"
              />
                  
              <List size="xl" center>
                {filteredRankings ? (
                  filteredRankings.length > 0 ? (
                    <List size="xl" center listStyleType="none">
                      {filteredRankings
                        .slice(
                          firstRankingIndex,
                          firstRankingIndex + rankingsPerPage
                        )
                        .map((ranking, index) => (
                          <div
                            index={index}
                            onClick={() =>
                              handleRankingCardClick(ranking[1], ranking[0])
                            }
                          >
                            <RankingCard
                              user={ranking[1]}
                              rank={ranking[0]}
                              key={index}
                              currUser={ranking[1].userid === userid}
                            />
                          </div>
                        ))}
                    </List>
                  ) : (
                    <p className="placeholder">No Participants</p>
                  )
                ) : (
                  <p className="placeholder">Loading...</p>
                )}
              </List>
            </Paper>

            <PaginationBar
              currentPage={currentLeaderboardPage}
              pageCount={leaderboardPageCount}
              handleOnClick={changeLeaderboardPage}
            />
          </Grid.Col>}
          {!isMobile && <Grid.Col span={5}>
            <UserContestCard
              user={selectedUserData ? selectedUserData.user : null}
              contestRanking={selectedUserData ? selectedUserData.rank : null}
              contestAttended="1"
              contestname={name}
            />
          </Grid.Col>}
          {isMobile && <Grid.Col span={12}>
            <UserContestCard
              user={selectedUserData ? selectedUserData.user : null}
              globalRanking={selectedUserData ? selectedUserData.rank : null}
              contestAttended="1"
              contestname={name}
            />
          </Grid.Col>}
        </Grid>
      </Stack>
    </div>
  );
}
